"use client";

// Replaces Next's own "Application error: a client-side exception has occurred", which says nothing to
// the person reading it and nothing to us either. This page states what happened in plain Danish, and
// reports the real error to the server so a crash on a device with no developer console can still be
// diagnosed. The details are only put on screen when the diagnose flag is set.
//
// Deliberately free of imports from the shared packages and the UI kit: an error page that depends on
// the modules that just failed to load is an error page that cannot render.

import { useEffect, useState } from "react";

const diagnoseStorageKey = "blizhub.customer.diagnose";

function isDiagnoseEnabled(): boolean {
  try {
    return window.location.search.indexOf("diagnose=1") >= 0 || window.localStorage.getItem(diagnoseStorageKey) === "1";
  } catch {
    return false;
  }
}

function describeError(error: Error & { digest?: string }): string {
  const digest = error.digest ? ` (digest ${error.digest})` : "";

  return `${error.name}: ${error.message}${digest}\n\n${error.stack ?? "(ingen stack)"}`;
}

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const [details, setDetails] = useState<string | null>(null);

  useEffect(() => {
    const description = describeError(error);

    if (isDiagnoseEnabled()) {
      setDetails(description);
    }

    try {
      const request = new XMLHttpRequest();

      request.open("POST", "/api/diagnostics/client-errors", true);
      request.setRequestHeader("content-type", "application/json");
      request.send(JSON.stringify({ message: description, source: "global-error", url: window.location.href }));
    } catch {
      // Reporting a crash must never be able to cause another one.
    }
  }, [error]);

  return (
    <html lang="da">
      <body style={{ background: "#fff", color: "#111", fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif", margin: 0, padding: "48px 24px" }}>
        <main style={{ margin: "0 auto", maxWidth: 560 }}>
          <h1 style={{ fontSize: 22, margin: "0 0 12px" }}>Siden kunne ikke vises</h1>
          <p style={{ lineHeight: 1.5, margin: "0 0 24px" }}>
            Der opstod en fejl i portalen. Fejlen er sendt videre automatisk.
          </p>
          <button
            onClick={() => reset()}
            style={{ background: "#111", border: 0, borderRadius: 8, color: "#fff", cursor: "pointer", fontSize: 15, padding: "10px 18px" }}
            type="button"
          >
            Prøv igen
          </button>
          {details ? (
            <pre style={{ background: "#111", borderRadius: 8, color: "#fff", fontSize: 12, lineHeight: 1.45, marginTop: 28, overflow: "auto", padding: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {details}
            </pre>
          ) : null}
        </main>
      </body>
    </html>
  );
}
